import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { taskProgress } from "resources/scheduler";
import { TaskProgressDc, ServerTaskStatus } from "@evergis/api";

import { usePrevValue } from "../usePrevValue";

type TaskStatus = TaskProgressDc["status"];

const COMPLETED_STATUSES: TaskStatus[] = [
  ServerTaskStatus.Completed,
  ServerTaskStatus.Failed,
  ServerTaskStatus.Canceled,
];
const PROCESSED_STATUSES: TaskStatus[] = [
  ServerTaskStatus.Scheduled,
  ServerTaskStatus.Planning,
  ServerTaskStatus.Executing,
];

const PROGRESS_REFETCH_TIMEOUT = 1000;

export function useProgress(taskId?: string) {
  const { data, refetch } = taskProgress.use({ input: taskId, waitInput: true });
  const [requested, setRequested] = useState(false);
  const [processed, setProcessed] = useState<number>(0);
  // TODO make only one TaskProgressStatus enum
  const [isTaskFailed, setFailed] = useState<boolean>();
  const [isTaskCompleted, setCompleted] = useState(false);
  const timer = useRef(null);
  const prevTaskId = usePrevValue(taskId);
  const isTaskExecuting = useMemo(() => !isTaskCompleted && !isTaskFailed, [isTaskFailed, isTaskCompleted]);
  const result = useMemo(() => data && data.taskResult, [data]);

  const reset = useCallback(() => {
    setCompleted(false);
    setRequested(false);
    setFailed(false);
    window.clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (prevTaskId !== taskId) {
      reset();
    }
  }, [prevTaskId, taskId]);

  useEffect(() => {
    if (data && data.status === "Failed") {
      setFailed(true);
    }

    if (data && !requested) {
      setRequested(true);
      !!data.currentStepAlreadyDone && setProcessed(data.currentStepAlreadyDone);
    }

    if (data?.taskResult?.outputObjectCount) {
      setProcessed(data.taskResult.outputObjectCount);
    }

    if (data && COMPLETED_STATUSES.includes(data.status)) {
      setCompleted(true);
    }
  }, [data]);

  useEffect(() => {
    if (requested && PROCESSED_STATUSES.includes(data.status)) {
      timer.current = window.setTimeout(() => {
        refetch();
      }, PROGRESS_REFETCH_TIMEOUT);

      setRequested(false);
    }
  }, [requested]);

  return useMemo(
    () => ({
      processed,
      isTaskCompleted,
      isTaskFailed,
      result,
      isTaskExecuting,
      reset,
    }),
    [processed, setProcessed, isTaskCompleted, isTaskFailed, isTaskExecuting, result],
  );
}
